/*
 * Author: Harper Yan
 * File Created: Tuesday, 24-07-16 11:04 am
 * Last Modified: Monday, 25-01-20 06:03 pm, by Harper Yan
 */

import {
  buttonBaseClasses,
  buttonClasses,
  svgIconClasses,
} from '@mui/material';
import {
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
} from '@mui/material/styles';

type ButtonThemeOptions = {
  defaultProps?: ComponentsProps['MuiButton'];
  styleOverrides?: ComponentsOverrides<Theme>['MuiButton'];
  variants?: ComponentsVariants<Theme>['MuiButton'];
};

const MuiButton: ButtonThemeOptions = {
  defaultProps: {
    size: 'large',
    disableElevation: true,
    disableTouchRipple: true,
    variant: 'contained',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: '30px',
      '&:lang(en)': {
        fontFamily: 'Inter, sans-serif',
      },
      '&:lang(zh)': {
        fontFamily: 'PingFang SC, sans-serif',
      },
    }),
    sizeLarge: () => ({
      padding: '12px',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
    }),
    sizeMedium: () => ({
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
    }),
    textPrimary: ({ theme }) => ({
      color: theme.palette.common.white,
      '&:active,:hover': {
        background: 'none',
        color: '#A993FF',
      },
    }),
    textSecondary: ({ theme }) => ({
      color: '#',
      '&:active': {
        background: 'none',
        color: theme.palette.primary.light,
      },
    }),
    containedPrimary: ({ theme }) => ({
      background: '#7E61F3',
      color: theme.palette.common.white,
      [`&.${buttonClasses.disabled}`]: {
        background: '#7E61F3',
        color: theme.palette.common.white,
        opacity: 0.5,
      },
      ':active': {
        background: '#5844AA',
        color: theme.palette.common.white,
      },
    }),
    containedSecondary: () => ({
      background: '#31323D',
      color: '#EAECEF',
      '&:hover': {
        background: '#313247',
        color: '#FFF',
      },
      ':disabled': {
        background: '#31323D',
        color: '#EAECEF',
        opacity: 0.5,
      },
      ':active': {
        background: '#24252D',
        color: '#EAECEF',
      },
    }),
  },
};

type IconButtonThemeOptions = {
  defaultProps?: ComponentsProps['MuiIconButton'];
  styleOverrides?: ComponentsOverrides<Theme>['MuiIconButton'];
  variants?: ComponentsVariants<Theme>['MuiIconButton'];
};

const MuiIconButton: IconButtonThemeOptions = {
  defaultProps: {
    size: 'small',
    disableTouchRipple: true,
  },
  styleOverrides: {
    sizeSmall: () => ({
      padding: '3px',
      [`& > .${svgIconClasses.root}`]: {
        fontSize: '18px',
      },
    }),
  },
};

export default {
  MuiButton,
  MuiIconButton,
};
